/**
 * LetterGenerator Utility Class v2.0
 *
 * Completely refactored letter generation system that fixes major issues:
 * - Y letter frequency reduced from 13.26% to 2-4% target range
 * - Mandatory E rule ensures E appears in every generation (when enabled)
 * - Level-specific rules for Easy/Normal/Hard gameplay
 * - Weighted selection system for better rarity balance
 * - Two-phase generation: mandatory letters first, then weighted random fill
 */

import { LEVEL_RULES } from '../config/letterSettings.js';

// Debug logging flag - set to true for detailed generation logs
const DEBUG_LETTER_GEN = false;

class LetterGenerator {
  /**
   * Generate random letters with new two-phase algorithm and level support
   * 
   * PHASE 1: Place mandatory letters (e.g., always include E)
   * PHASE 2: Fill remaining slots with weighted random selection
   * 
   * @param {Object} settingsManager - Settings manager instance for dependency injection
   * @param {string} level - Difficulty level: 'easy', 'normal', 'hard' (default: 'normal')
   * @returns {Array} Array of 15 letter objects with improved distribution
   */
  static generateRandomLetters(settingsManager, level = 'normal') {
    // Validate level and get rules
    const rules = LEVEL_RULES[level] || LEVEL_RULES.normal;
    const settings = settingsManager.getSettings();
    
    LetterGenerator._debugLog('init', 'Starting letter generation', { level, rules });
    
    // Helper function to create letter object with settings info
    function createLetterObject(char) {
      const letterInfo = settingsManager.getLetterInfo(char);
      return {
        char,
        points: letterInfo ? letterInfo.points : 1,
        inBar: false,
        isVowel: letterInfo ? letterInfo.isVowel : false,
        isRare: letterInfo ? letterInfo.isRare : false,
        isSuperRare: letterInfo ? letterInfo.super_rare : false
      };
    }

    // Edge case: empty settings
    const allLetters = Object.keys(settings);
    if (allLetters.length === 0) {
      LetterGenerator._debugLog('edge', 'Empty settings, returning default A letters');
      return Array(15).fill(null).map(() => ({
        char: 'A', points: 1, inBar: false, isVowel: true, isRare: false, isSuperRare: false
      }));
    }

    // Initialize letter counting system with Map for better performance
    const letterCounts = new Map();
    const result = [];
    
    // Get letter pools
    const { notRareLetters, rareLetters, superRareLetters, allVowels, allConsonants } = 
      LetterGenerator._getLetterPools(settings);
    
    LetterGenerator._debugLog('pools', 'Letter pools created', {
      notRare: notRareLetters.length,
      rare: rareLetters.length, 
      superRare: superRareLetters.length,
      vowels: allVowels.length,
      consonants: allConsonants.length
    });

    // === PHASE 1: MANDATORY LETTERS ===
    if (rules.mandatoryE) {
      result.push(createLetterObject('E'));
      letterCounts.set('E', 1);
      LetterGenerator._debugLog('phase1', 'Placed mandatory E letter');
    }

    // === PHASE 2: WEIGHTED RANDOM FILL ===
    const remainingSlots = 15 - result.length;
    const targetRare = Math.min(rules.rare, remainingSlots);
    const targetNotRare = remainingSlots - targetRare;
    
    LetterGenerator._debugLog('phase2', 'Starting weighted fill', {
      remainingSlots,
      targetRare,
      targetNotRare
    });

    // Fill rare letters with weighted selection
    for (let i = 0; i < targetRare; i++) {
      const selected = LetterGenerator._selectWeightedLetter(
        rareLetters, settings, letterCounts, rules.duplicateCap
      );
      if (selected) {
        result.push(createLetterObject(selected));
        letterCounts.set(selected, (letterCounts.get(selected) || 0) + 1);
        LetterGenerator._debugLog('phase2', `Selected rare letter: ${selected}`);
      }
    }

    // Fill not-rare letters with weighted selection
    for (let i = 0; i < targetNotRare; i++) {
      const selected = LetterGenerator._selectWeightedLetter(
        notRareLetters, settings, letterCounts, rules.duplicateCap
      );
      if (selected) {
        result.push(createLetterObject(selected));
        letterCounts.set(selected, (letterCounts.get(selected) || 0) + 1);
        LetterGenerator._debugLog('phase2', `Selected common letter: ${selected}`);
      }
    }

    // Safety: Fill any remaining slots (shouldn't happen but good to have)
    while (result.length < 15) {
      const fallback = LetterGenerator._selectFallbackLetter(
        allLetters, letterCounts, rules.duplicateCap
      );
      if (fallback) {
        result.push(createLetterObject(fallback));
        letterCounts.set(fallback, (letterCounts.get(fallback) || 0) + 1);
        LetterGenerator._debugLog('fallback', `Added fallback letter: ${fallback}`);
      } else {
        break; // Prevent infinite loop
      }
    }

    // Shuffle the letters array to randomize positions
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }

    // Apply super-rare upgrades (existing logic)
    const finalResult = LetterGenerator._applySuperRareUpgrades(
      result, superRareLetters, settingsManager, createLetterObject
    );
    
    LetterGenerator._debugLog('final', 'Generation complete', {
      totalLetters: finalResult.length,
      letterDistribution: LetterGenerator._getDistributionSummary(finalResult)
    });
    
    return finalResult;
  }

  /**
   * Get letter pools organized by rarity
   * @param {Object} settings - Letter settings
   * @returns {Object} Letter pools organized by rarity and type
   * @private
   */
  static _getLetterPools(settings) {
    const allLetters = Object.keys(settings);
    const notRareLetters = allLetters.filter(char => !settings[char].isRare && !settings[char].super_rare);
    const rareLetters = allLetters.filter(char => settings[char].isRare && !settings[char].super_rare);
    const superRareLetters = allLetters.filter(char => settings[char].super_rare);
    const allVowels = allLetters.filter(char => settings[char].isVowel);
    const allConsonants = allLetters.filter(char => !settings[char].isVowel);
    
    return { notRareLetters, rareLetters, superRareLetters, allVowels, allConsonants };
  }

  /**
   * Select a letter using weighted random selection with duplication limits
   * @param {Array} pool - Available letters to choose from
   * @param {Object} settings - Letter settings containing weights
   * @param {Map} letterCounts - Current letter usage counts
   * @param {number} duplicateCap - Maximum duplicates allowed per letter
   * @returns {string|null} Selected letter or null if none available
   * @private
   */
  static _selectWeightedLetter(pool, settings, letterCounts, duplicateCap) {
    // Filter pool to only available letters (under duplication cap)
    const availableLetters = pool.filter(char => 
      (letterCounts.get(char) || 0) < duplicateCap
    );
    
    if (availableLetters.length === 0) {
      LetterGenerator._debugLog('weighted', 'No available letters in pool', { poolSize: pool.length });
      return null;
    }
    
    // Create weighted selection array
    const weightedLetters = [];
    availableLetters.forEach(char => {
      const letterInfo = settings[char];
      const weight = letterInfo.weight || 1.0;
      const weightedCount = Math.max(1, Math.round(weight * 10)); // Convert weight to count
      
      for (let i = 0; i < weightedCount; i++) {
        weightedLetters.push(char);
      }
    });
    
    if (weightedLetters.length === 0) {
      return availableLetters[0]; // Fallback to first available
    }
    
    const selected = weightedLetters[Math.floor(Math.random() * weightedLetters.length)];
    LetterGenerator._debugLog('weighted', 'Selected letter', {
      letter: selected,
      weight: settings[selected].weight,
      currentCount: letterCounts.get(selected) || 0
    });
    
    return selected;
  }

  /**
   * Select a fallback letter when normal selection fails
   * @param {Array} allLetters - All available letters
   * @param {Map} letterCounts - Current letter usage counts
   * @param {number} duplicateCap - Maximum duplicates allowed per letter
   * @returns {string|null} Selected fallback letter or null
   * @private
   */
  static _selectFallbackLetter(allLetters, letterCounts, duplicateCap) {
    // Try to find any letter under the cap
    const underCapLetters = allLetters.filter(char => 
      (letterCounts.get(char) || 0) < duplicateCap
    );
    
    if (underCapLetters.length > 0) {
      return underCapLetters[Math.floor(Math.random() * underCapLetters.length)];
    }
    
    // If all letters are at cap, pick the one with minimum count
    let minCount = Infinity;
    let minLetter = null;
    
    allLetters.forEach(char => {
      const count = letterCounts.get(char) || 0;
      if (count < minCount) {
        minCount = count;
        minLetter = char;
      }
    });
    
    return minLetter;
  }

  /**
   * Debug logging utility
   * @param {string} phase - Current phase of generation
   * @param {string} message - Log message
   * @param {Object} data - Additional data to log
   * @private
   */
  static _debugLog(phase, message, data = {}) {
    if (DEBUG_LETTER_GEN) {
      console.debug(`[LetterGen:${phase}] ${message}`, data);
    }
  }

  /**
   * Get distribution summary for debugging
   * @param {Array} letters - Generated letters
   * @returns {Object} Distribution summary
   * @private
   */
  static _getDistributionSummary(letters) {
    const summary = {
      vowels: 0,
      consonants: 0,
      rare: 0,
      common: 0,
      letterCounts: {}
    };
    
    letters.forEach(letter => {
      if (letter.isVowel) summary.vowels++;
      else summary.consonants++;
      
      if (letter.isRare || letter.isSuperRare) summary.rare++;
      else summary.common++;
      
      summary.letterCounts[letter.char] = (summary.letterCounts[letter.char] || 0) + 1;
    });
    
    return summary;
  }

  /**
   * Apply super rare upgrades to generated letters
   * @param {Array} letters - Array of letter objects
   * @param {Array} superRareLetters - Array of super rare letter characters
   * @param {Object} settingsManager - Settings manager instance
   * @param {Function} createLetterObject - Function to create letter objects
   * @returns {Array} Letters with super rare upgrades applied
   * @private
   */
  static _applySuperRareUpgrades(letters, superRareLetters, settingsManager, createLetterObject) {
    // Super rare upgrade mechanism: 10% chance for exactly ONE super rare letter per generation
    // Only apply if super-rare letters are available in settings
    // Apply upgrades while respecting consonant duplication limits
    // Ensure maximum 1 super-rare per generation

    // First check if there's already a super-rare letter in the set
    const hasExistingSuperRare = letters.some(letter => letter.isSuperRare);

    if (superRareLetters.length > 0 && !hasExistingSuperRare && Math.random() < 0.1) { // 10% chance for any super rare upgrade
      // Track consonant and vowel counts to ensure upgrades don't violate limits
      let currentConsonantCounts = {};
      let currentVowelCounts = {};
      letters.forEach((letter) => {
        if (!letter.isVowel) {
          currentConsonantCounts[letter.char] = (currentConsonantCounts[letter.char] || 0) + 1;
        } else {
          currentVowelCounts[letter.char] = (currentVowelCounts[letter.char] || 0) + 1;
        }
      });

      // Select a random position to upgrade
      const upgradePosition = Math.floor(Math.random() * letters.length);
      const superRareChar = superRareLetters[Math.floor(Math.random() * superRareLetters.length)];
      const superRareLetterInfo = settingsManager.getLetterInfo(superRareChar);
      const isUpgradeConsonant = superRareLetterInfo ? !superRareLetterInfo.isVowel : false;

      if (isUpgradeConsonant) {
        // For consonant upgrades, check duplication limits
        const currentChar = letters[upgradePosition].char;
        const currentIsConsonant = !letters[upgradePosition].isVowel;

        // Calculate what the counts would be after the upgrade
        let newConsonantCounts = { ...currentConsonantCounts };

        // Remove the current letter from count if it's a consonant
        if (currentIsConsonant) {
          newConsonantCounts[currentChar] = (newConsonantCounts[currentChar] || 1) - 1;
        }

        // Add the new super-rare consonant to count
        newConsonantCounts[superRareChar] = (newConsonantCounts[superRareChar] || 0) + 1;

        // Only apply upgrade if it doesn't violate the limit
        if (newConsonantCounts[superRareChar] <= 2) {
          letters[upgradePosition] = createLetterObject(superRareChar);
        }
        // If upgrade would violate limit, skip this upgrade (no super rare this generation)
      } else {
        // Vowel upgrades now also have limits (max 2 of same vowel)
        const currentChar = letters[upgradePosition].char;
        const currentIsConsonant = !letters[upgradePosition].isVowel;

        // Calculate what the counts would be after the upgrade
        let newVowelCounts = { ...currentVowelCounts };
        let newConsonantCounts = { ...currentConsonantCounts };

        // Remove the current letter from count
        if (currentIsConsonant) {
          newConsonantCounts[currentChar] = (newConsonantCounts[currentChar] || 1) - 1;
        } else {
          newVowelCounts[currentChar] = (newVowelCounts[currentChar] || 1) - 1;
        }

        // Add the new super-rare vowel to count
        newVowelCounts[superRareChar] = (newVowelCounts[superRareChar] || 0) + 1;

        // Only apply upgrade if it doesn't violate the vowel limit
        if (newVowelCounts[superRareChar] <= 2) {
          letters[upgradePosition] = createLetterObject(superRareChar);
        }
        // If upgrade would violate limit, skip this upgrade (no super rare this generation)
      }
    }

    return letters;
  }

  /**
   * Generate a single random letter for buying
   * Uses simplified 50/50 vowel/consonant distribution that ignores rarity settings and pool composition
   * @param {Object} settingsManager - Settings manager instance for dependency injection
   * @param {Array} currentLetters - Optional array of current letters (ignored in simplified version)
   * @returns {Object} Single letter object with simplified distribution
   */
  static generateSingleLetter(settingsManager, currentLetters = []) {
    const settings = settingsManager.getSettings();
    const allLetters = Object.keys(settings);

    // Handle edge case where no letters are available
    if (allLetters.length === 0) {
      return {
        char: 'A',
        points: 1,
        inBar: false,
        isVowel: true,
        isRare: false,
        isSuperRare: false
      };
    }

    // Helper function to create letter object with settings info
    function createLetterObject(char) {
      const letterInfo = settingsManager.getLetterInfo(char);
      return {
        char,
        points: letterInfo ? letterInfo.points : 1,
        inBar: false,
        isVowel: letterInfo ? letterInfo.isVowel : false,
        isRare: letterInfo ? letterInfo.isRare : false,
        isSuperRare: letterInfo ? letterInfo.super_rare : false
      };
    }

    // Separate all vowels and consonants (ignoring rarity)
    const allVowels = allLetters.filter(char => settings[char].isVowel);
    const allConsonants = allLetters.filter(char => !settings[char].isVowel);

    // Simple 50/50 distribution - exactly 50% chance for vowel, 50% for consonant
    let selectedChar;

    if (allVowels.length === 0 && allConsonants.length === 0) {
      // Edge case: no letters available, fallback to 'A'
      selectedChar = 'A';
    } else if (allVowels.length === 0) {
      // No vowels available, select random consonant
      selectedChar = allConsonants[Math.floor(Math.random() * allConsonants.length)];
    } else if (allConsonants.length === 0) {
      // No consonants available, select random vowel
      selectedChar = allVowels[Math.floor(Math.random() * allVowels.length)];
    } else {
      // Both vowels and consonants available - 50/50 chance
      if (Math.random() < 0.5) {
        // Select random vowel
        selectedChar = allVowels[Math.floor(Math.random() * allVowels.length)];
      } else {
        // Select random consonant
        selectedChar = allConsonants[Math.floor(Math.random() * allConsonants.length)];
      }
    }

    return createLetterObject(selectedChar);
  }

  /**
   * Generate letter pool for a specific level with target word
   * Ensures target word letters are present + random camouflage letters
   *
   * @param {string} targetWord - The word the player must spell (uppercase)
   * @param {Object} settingsManager - Settings manager instance
   * @param {number} camouflageRatio - Ratio of distracting letters (0.0-1.0)
   * @param {string} language - Language code ('en' or 'da') to filter appropriate letters
   * @returns {Array} Array of 18 letter objects (15 visible + 3 empty slots)
   */
  static generateLevelLetters(targetWord, settingsManager, camouflageRatio = 0.5, language = 'en') {
    const settings = settingsManager.getSettings();

    // Helper function to create letter object
    function createLetterObject(char) {
      const letterInfo = settingsManager.getLetterInfo(char);
      return {
        char,
        points: letterInfo ? letterInfo.points : 1,
        inBar: false,
        isVowel: letterInfo ? letterInfo.isVowel : false,
        isRare: letterInfo ? letterInfo.isRare : false,
        isSuperRare: letterInfo ? letterInfo.super_rare : false
      };
    }

    // Helper function to filter letters by language
    function isLetterValidForLanguage(char, lang) {
      const danishSpecialLetters = ['Æ', 'Ø', 'Å'];
      const isDanishSpecial = danishSpecialLetters.includes(char);

      if (lang === 'da') {
        // Danish: allow all letters (A-Z + Æ, Ø, Å)
        return true;
      } else {
        // English: only A-Z (exclude Danish special letters)
        return !isDanishSpecial;
      }
    }

    // Extract target word letters
    const targetLetters = targetWord.split('').map(char => createLetterObject(char));
    const targetLength = targetLetters.length;

    // Calculate how many camouflage letters we need
    // We want 18 total visible letters (all slots filled)
    const totalVisible = 18;
    const camouflageCount = totalVisible - targetLength;

    // Track letter usage to prevent excessive duplicates
    // Count letters already in target word
    const letterCounts = new Map();
    targetWord.split('').forEach(char => {
      letterCounts.set(char, (letterCounts.get(char) || 0) + 1);
    });

    // Generate camouflage letters
    const camouflageLetters = [];

    // Determine how many should be "helpful" vs "distracting"
    const distractingCount = Math.floor(camouflageCount * camouflageRatio);
    const helpfulCount = camouflageCount - distractingCount;

    // Get all available letters filtered by language
    const allLetters = Object.keys(settings).filter(char => isLetterValidForLanguage(char, language));

    // Duplicate cap: max 3 of same letter in entire pool (target + camouflage)
    const duplicateCap = 3;

    // Generate "helpful" letters (common letters using weighted selection)
    for (let i = 0; i < helpfulCount; i++) {
      // Prefer common letters (not rare, not super rare)
      const commonLetters = allLetters.filter(char =>
        !settings[char].isRare && !settings[char].super_rare
      );

      if (commonLetters.length > 0) {
        // Use weighted selection to respect letter weights
        const selectedChar = LetterGenerator._selectWeightedLetter(
          commonLetters,
          settings,
          letterCounts,
          duplicateCap
        );

        if (selectedChar) {
          camouflageLetters.push(createLetterObject(selectedChar));
          letterCounts.set(selectedChar, (letterCounts.get(selectedChar) || 0) + 1);
          LetterGenerator._debugLog('level', `Selected helpful letter: ${selectedChar}`);
        } else {
          // Fallback to uniform random if weighted selection fails
          const randomChar = commonLetters[Math.floor(Math.random() * commonLetters.length)];
          camouflageLetters.push(createLetterObject(randomChar));
          letterCounts.set(randomChar, (letterCounts.get(randomChar) || 0) + 1);
          LetterGenerator._debugLog('level', `Fallback helpful letter: ${randomChar}`);
        }
      } else {
        // Fallback to any letter with weighted selection
        const selectedChar = LetterGenerator._selectWeightedLetter(
          allLetters,
          settings,
          letterCounts,
          duplicateCap
        );

        if (selectedChar) {
          camouflageLetters.push(createLetterObject(selectedChar));
          letterCounts.set(selectedChar, (letterCounts.get(selectedChar) || 0) + 1);
        }
      }
    }

    // Generate "distracting" letters using weighted selection
    // This respects letter weights (e.g., Danish special characters have lower weight)
    for (let i = 0; i < distractingCount; i++) {
      // Use weighted selection to respect letter frequency settings
      const selectedChar = LetterGenerator._selectWeightedLetter(
        allLetters,
        settings,
        letterCounts,
        duplicateCap
      );

      if (selectedChar) {
        camouflageLetters.push(createLetterObject(selectedChar));
        letterCounts.set(selectedChar, (letterCounts.get(selectedChar) || 0) + 1);
        LetterGenerator._debugLog('level', `Selected distracting letter: ${selectedChar}`);
      } else {
        // Fallback to uniform random if weighted selection fails
        const randomChar = allLetters[Math.floor(Math.random() * allLetters.length)];
        camouflageLetters.push(createLetterObject(randomChar));
        letterCounts.set(randomChar, (letterCounts.get(randomChar) || 0) + 1);
        LetterGenerator._debugLog('level', `Fallback distracting letter: ${randomChar}`);
      }
    }

    // Combine target letters and camouflage letters
    const allPoolLetters = [...targetLetters, ...camouflageLetters];

    // Shuffle to hide the target word
    for (let i = allPoolLetters.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allPoolLetters[i], allPoolLetters[j]] = [allPoolLetters[j], allPoolLetters[i]];
    }

    // Create 18-slot array (all filled)
    const result = Array(18).fill(null);
    for (let i = 0; i < allPoolLetters.length && i < 18; i++) {
      result[i] = allPoolLetters[i];
    }

    LetterGenerator._debugLog('level', `Generated level letters for "${targetWord}"`, {
      targetLength,
      camouflageCount,
      helpfulCount,
      distractingCount,
      totalLetters: allPoolLetters.length
    });

    return result;
  }

  /**
   * Shuffle array in place (Fisher-Yates algorithm)
   * @param {Array} array - Array to shuffle
   * @returns {Array} - Shuffled array
   */
  static _shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }
}

export default LetterGenerator;
